/** The chat unit (title + card) never scales below this — past it the transcript
 would be unreadable; the bottom clips instead on absurdly short viewports. */
export const CHAT_FIT_MIN_SCALE = 0.6;
export const CHAT_FIT_BOTTOM_MARGIN_PX = 24;
export const CHAT_FIT_TOP_MARGIN_PX = 12;

/** Fit-threshold and first-paint fallback for the chat card top, as a fraction of
 the 100svh scene. Once measured, the rest pose is vertically centered. */
export const CHAT_FIT_REST_TOP_RATIO = 0.28;

/* The chat unit drifts up as the scene deepens (depth parallax). The drift is a fixed
 px cap on tall stages but must shrink with the stage — on short viewports a fixed
 150px drift exceeds the fitted headroom and pushes the title off the top. */
const CHAT_DRIFT_RATIO = 0.167;
const CHAT_DRIFT_CAP_DESKTOP_PX = 150;
const CHAT_DRIFT_CAP_MOBILE_PX = 110;

export function resolveChatDriftMax(stageHeight: number, isMobile: boolean): number {
	const cap = isMobile ? CHAT_DRIFT_CAP_MOBILE_PX : CHAT_DRIFT_CAP_DESKTOP_PX;
	return Math.min(cap, stageHeight * CHAT_DRIFT_RATIO);
}

export type TChatFitInput = {
	stageHeight: number;
	/** Natural (unscaled) height of the fully-revealed chat card. */
	cardHeight: number;
	/** Natural height of the title block pinned above the card. */
	titleHeight: number;
	/** Design top offset of the card in px (used as the scale-threshold, not the rest pose). */
	restTop: number;
};

export type TChatFit = {
	scale: number;
	/** Card top in px. Null only when measurements are not ready. */
	top: number | null;
};

/* The chat card grows as the transcript reveals; on short viewports the full
 transcript would overflow the pinned stage. Scale the whole unit down around its
 top edge and re-center it, so the demo stays complete instead of clipping.

 When the full unit fits, pin the card so title + card are vertically centered
 in the stage. The live transcript is shorter at first and grows down into that
 reserved space — centering the live card would lift the block as messages land. */
export function resolveChatFit({ stageHeight, cardHeight, titleHeight, restTop }: TChatFitInput): TChatFit {
	if (stageHeight <= 0 || cardHeight <= 0) return { scale: 1, top: null };

	const available = stageHeight - restTop - CHAT_FIT_BOTTOM_MARGIN_PX;
	if (cardHeight <= available) {
		if (titleHeight <= 0) return { scale: 1, top: null };
		const unitHeight = titleHeight + cardHeight;
		const visualTop = Math.max(CHAT_FIT_TOP_MARGIN_PX, (stageHeight - unitHeight) / 2);
		const cardTop = visualTop + titleHeight;
		if (cardTop + cardHeight + CHAT_FIT_BOTTOM_MARGIN_PX <= stageHeight) {
			return { scale: 1, top: cardTop };
		}
		return { scale: 1, top: null };
	}

	const scale = Math.max(CHAT_FIT_MIN_SCALE, available / cardHeight);
	const unitHeight = (titleHeight + cardHeight) * scale;
	const visualTop = Math.max(CHAT_FIT_TOP_MARGIN_PX, (stageHeight - unitHeight) / 2);
	return { scale, top: visualTop + titleHeight * scale };
}
